import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { circularVelocity, elementsFromState, periapsisRadius } from '@/universe/OrbitalElements';
import { BaseMacro, MacroContext, MacroEstimate, MacroStatus } from './Macro';

const g0 = 9.80665;

/** Builds an attitude quaternion pointing the nose along `direction`. */
function attitudeFor(direction: Vec3, up: Vec3): Quat {
  const forward = direction.normalized();
  let reference = up;
  if (Math.abs(forward.dot(reference)) > 0.985) reference = Vec3.anyPerpendicular(forward);
  const right = Vec3.crossVectors(reference, forward).normalize();
  const trueUp = Vec3.crossVectors(forward, right).normalize();
  return Quat.fromBasis(right, trueUp, forward);
}

/**
 * Powered landing.
 *
 * Three physical phases, each with a clear termination condition:
 *
 *   Braking   kill horizontal (surface-relative) velocity by burning against
 *             the surface-relative retrograde vector.
 *   Descent   fall under control, targeting a descent rate derived from a
 *             constant-deceleration stopping profile: at every altitude the
 *             vessel keeps enough braking authority to arrive at zero.
 *   Touchdown hold a slow, fixed descent rate until the gear makes contact.
 *
 * The descent-rate profile is the important part. It is computed from the
 * deceleration actually available (engines plus field drive, minus gravity), so
 * a heavy vessel on a high-gravity world automatically flies a shallower,
 * slower approach than a light one on a moon, without any per-body tuning.
 */
export class LandingMacro extends BaseMacro {
  readonly id = 'land';
  readonly name = 'Powered Landing';
  readonly description =
    'Cancels horizontal velocity, flies a controlled descent profile sized to the available deceleration, and ' +
    'sets down within landing gear limits.';
  readonly phases = ['Verify', 'Braking', 'Descent', 'Touchdown', 'Complete'];

  /** Descent rate held during the final metres, m/s. */
  touchdownRate = 1.6;

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, env, body } = ctx;
    const g = env.gravity.length();
    const mass = ship.massProperties.total;

    const landingThrust = ship.landingThrusters.reduce((s, e) => s + e.def.maxThrust, 0);
    const mainThrust = ship.mainEngines.reduce((s, e) => s + e.def.maxThrust, 0);
    const fieldAccel = ship.fieldDrive ? ship.fieldDrive.availableAcceleration(g) : 0;
    const decel = (landingThrust + mainThrust) / mass + fieldAccel - g;

    const surfaceSpeed = Vec3.subVectors(
      ship.body.velocity,
      body.surfaceVelocityAt(ship.body.position),
    ).length();

    const failureModes = [
      'Insufficient deceleration authority for the local gravity',
      'Propellant exhausted during descent',
      'Touchdown speed exceeds landing gear limit',
      'Terrain slope exceeds stable landing angle',
    ];

    if (decel <= 0.3) {
      return {
        duration: Infinity,
        propellant: Infinity,
        deltaV: Infinity,
        peakAcceleration: (landingThrust + mainThrust) / mass,
        heatLoad: 0,
        failureModes,
        blocked: true,
        blockedReason: `Available deceleration does not exceed local gravity (${g.toFixed(2)} m/s^2)`,
      };
    }

    const deltaV = surfaceSpeed + Math.sqrt(2 * Math.max(1, decel) * Math.max(0, env.altitude)) * 0.6 + g * 12;
    const isp = Math.max(1, ...ship.landingThrusters.map((e) => e.def.ispVacuum), ...ship.mainEngines.map((e) => e.def.ispVacuum));
    const propellant = mass * (1 - Math.exp(-deltaV / (isp * g0)));

    const available = ship.totalDeltaV();
    return {
      duration: surfaceSpeed / Math.max(0.5, decel) + Math.max(0, env.altitude) / 25 + 30,
      propellant,
      deltaV,
      peakAcceleration: (landingThrust + mainThrust) / mass + fieldAccel,
      heatLoad: (ship.lastAero?.heatFlux ?? 0) * 1.4,
      failureModes,
      blocked: deltaV > available,
      blockedReason:
        deltaV > available ? `Requires ${deltaV.toFixed(0)} m/s; ${available.toFixed(0)} m/s available` : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    ctx.ship.controls.landingThrustersArmed = true;
    this.advance(1, 'Braking');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);

    const { ship, computer, env, body } = ctx;

    if (ship.contact.landed) {
      this.advance(4, 'Landed');
      this.releaseControls(ctx);
      this.statusText = `Touchdown at ${ship.contact.peakImpact.toFixed(2)} m/s`;
      return 'complete';
    }

    // Surface-relative velocity is what matters near the ground.
    const surfaceVel = Vec3.subVectors(ship.body.velocity, body.surfaceVelocityAt(ship.body.position));
    const verticalSpeed = surfaceVel.dot(env.up);
    const horizontal = surfaceVel.rejectFromUnit(env.up);
    const horizontalSpeed = horizontal.length();

    const g = env.gravity.length();
    const mass = ship.massProperties.total;
    const fieldAccel = ship.fieldDrive ? ship.fieldDrive.availableAcceleration(g) : 0;
    const thrustAccel =
      (ship.landingThrusters.reduce((s, e) => s + e.maxThrustAt({ pressure: env.pressure, density: env.density }), 0) +
        ship.mainEngines.reduce((s, e) => s + e.maxThrustAt({ pressure: env.pressure, density: env.density }), 0)) /
      mass;
    const netDecel = Math.max(0.4, (thrustAccel + fieldAccel - g) * (1 - this.safety.margin * 0.4));

    switch (this.phaseIndex) {
      case 1: {
        // Kill horizontal velocity while holding altitude.
        computer.holds.hoverAssist = true;
        computer.holds.altitudeHold = false;
        computer.holds.verticalSpeedHold = false;
        computer.holds.velocityHold = false;
        computer.setAlignment('surfaceUp');

        if (horizontalSpeed > 4) {
          // Point the main engine against the horizontal component. The vessel
          // pitches over, so the hover assist automatically increases thrust to
          // compensate for the reduced vertical component.
          const brakeDir = horizontal.clone().mulScalar(-1 / horizontalSpeed);
          const tilt = clamp(horizontalSpeed / 220, 0, 0.6);
          const noseDir = new Vec3().addScaled(env.up, 1 - tilt).addScaled(brakeDir, tilt).normalize();
          computer.holds.alignment = 'custom';
          computer.customAttitude.copy(attitudeFor(noseDir, env.up));
          ship.controls.mainEngineArmed = true;
          ship.controls.throttle = clamp01(horizontalSpeed / 120);
        } else {
          ship.controls.throttle = 0;
          ship.controls.mainEngineArmed = false;
          computer.setAlignment('surfaceUp');
          this.advance(2, 'Descent');
        }
        this.statusText = `Braking: ${horizontalSpeed.toFixed(1)} m/s lateral, ${env.altitude.toFixed(0)} m altitude`;
        return 'running';
      }

      case 2: {
        computer.setAlignment('surfaceUp');
        computer.holds.hoverAssist = false;
        computer.holds.verticalSpeedHold = true;
        this.holdSurfaceVelocity(ctx);

        // Stopping profile: the fastest descent from which we can still stop.
        const safeAltitude = Math.max(0, env.altitude - 12);
        const maxRate = Math.sqrt(2 * netDecel * safeAltitude);
        computer.holds.verticalSpeedTarget = -Math.min(maxRate * 0.8, 90);

        if (horizontalSpeed > 12) {
          this.advance(1, 'Braking (lateral drift)');
          return 'running';
        }

        if (env.altitude < 30) {
          this.advance(3, 'Touchdown');
        }
        this.statusText = `Descent: ${env.altitude.toFixed(0)} m, ${verticalSpeed.toFixed(1)} m/s (target ${computer.holds.verticalSpeedTarget.toFixed(1)})`;
        return 'running';
      }

      case 3: {
        computer.setAlignment('surfaceUp');
        computer.holds.verticalSpeedHold = true;
        computer.holds.verticalSpeedTarget = -this.touchdownRate;
        this.holdSurfaceVelocity(ctx);

        if (ship.massProperties.fuel <= 0.5 && env.altitude > 5) {
          return this.fail(ctx, 'Propellant exhausted during touchdown');
        }
        this.statusText = `Touchdown: ${env.altitude.toFixed(1)} m, ${verticalSpeed.toFixed(2)} m/s`;
        return 'running';
      }

      default:
        return 'complete';
    }
  }

  /**
   * Keeps RCS quietly cancelling horizontal drift relative to the rotating
   * surface below, throughout Descent and Touchdown.
   *
   * Braking (phase 1) only zeroes horizontal velocity once, against the
   * surface velocity at that instant. On a rotating body that target keeps
   * moving, so without this the ship coasts in a straight inertial line while
   * the landing site sweeps out from under it — invisible on a body spun up
   * to match the ship at spawn, but obvious after an approach from orbit.
   * The vertical component is left at the ship's current rate so this never
   * fights `verticalSpeedHold`, which owns descent rate via the main/landing
   * thrusters.
   */
  private holdSurfaceVelocity(ctx: MacroContext): void {
    const { ship, computer, env, body } = ctx;
    computer.holds.velocityHold = true;
    const verticalNow = ship.body.velocity.dot(env.up);
    computer.holds.velocityTarget
      .copy(body.surfaceVelocityAt(ship.body.position))
      .addScaled(env.up, verticalNow);
  }
}

/**
 * Circularise at the next apsis using the manoeuvre planner, so the burn the
 * macro executes is the same object the player could have planned by hand.
 */
export class CirculariseMacro extends BaseMacro {
  readonly id = 'circularise';
  readonly name = 'Circularise Orbit';
  readonly description = 'Plans and executes a burn at the next apsis to make the orbit circular.';
  readonly phases = ['Plan', 'Align', 'Burn', 'Complete'];

  at: 'apoapsis' | 'periapsis' = 'apoapsis';
  private nodeId: string | null = null;

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, body } = ctx;
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
    const r = this.at === 'apoapsis' ? elements.semiMajorAxis * (1 + elements.eccentricity) : periapsisRadius(elements);
    if (!Number.isFinite(r) || r <= 0) {
      return {
        duration: 0,
        propellant: 0,
        deltaV: 0,
        peakAcceleration: 0,
        heatLoad: 0,
        failureModes: ['Trajectory has no reachable apsis'],
        blocked: true,
        blockedReason: 'Trajectory has no reachable apoapsis',
      };
    }
    const vAtApsis = Math.sqrt(body.mu * (2 / r - 1 / elements.semiMajorAxis));
    const deltaV = Math.abs(circularVelocity(body.mu, r) - vAtApsis);
    const isp = Math.max(1, ...ship.mainEngines.map((e) => e.def.ispVacuum));
    const mass = ship.massProperties.total;
    const propellant = mass * (1 - Math.exp(-deltaV / (isp * g0)));
    const thrust = ship.mainEngines.reduce((s, e) => s + e.def.maxThrust, 0);

    return {
      duration: (propellant / Math.max(1e-6, thrust / (isp * g0))) + 60,
      propellant,
      deltaV,
      peakAcceleration: thrust / Math.max(1, mass - propellant),
      heatLoad: 0,
      failureModes: ['Insufficient delta-v', 'Engine failure during burn'],
      blocked: deltaV > ship.totalDeltaV(),
      blockedReason: deltaV > ship.totalDeltaV() ? 'Insufficient delta-v' : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    const node = ctx.planner.planCircularisation(ctx.ship, ctx.body, ctx.t, this.at);
    if (!node) {
      this.status = 'failed';
      this.statusText = 'Could not plan a circularisation burn';
      return;
    }
    this.nodeId = node.id;
    ctx.planner.beginExecution(node);
    ctx.ship.controls.mainEngineArmed = true;
    this.advance(1, 'Aligning for burn');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);
    if (!this.nodeId) return this.fail(ctx, 'No manoeuvre node');

    const result = ctx.planner.updateExecution(ctx.ship, ctx.body, ctx.t, ctx.dt);
    if (result.complete) {
      this.advance(3, 'Complete');
      this.releaseControls(ctx);
      this.statusText = 'Circularisation complete';
      return 'complete';
    }

    ctx.computer.maneuverDirection = result.direction;
    ctx.computer.setAlignment('maneuver');
    ctx.ship.controls.mainEngineArmed = result.throttle > 0;
    ctx.ship.controls.throttle = result.throttle;

    if (result.throttle > 0) {
      this.advance(2, `Burning: ${ctx.planner.remainingDeltaV.toFixed(1)} m/s remaining`);
    } else {
      this.advance(1, `Aligned, waiting for burn window`);
    }
    return 'running';
  }
}

/**
 * Rendezvous and docking assist.
 *
 * Closes on a target using a distance-dependent approach speed, nulls the
 * relative velocity, and holds a slow final closure rate. It never snaps the
 * vessel into place: the docking constraint only engages when the real relative
 * position, velocity and alignment are all inside the capture envelope.
 */
export class DockingMacro extends BaseMacro {
  readonly id = 'dock';
  readonly name = 'Docking Approach';
  readonly description =
    'Matches velocity with the selected target, closes along the approach axis at a safe rate, and holds ' +
    'alignment inside the capture envelope.';
  readonly phases = ['Verify', 'Match velocity', 'Approach', 'Final', 'Complete'];

  /** Capture envelope: distance, relative speed and alignment. */
  captureDistance = 6;
  captureSpeed = 0.3;

  estimate(ctx: MacroContext): MacroEstimate {
    const { computer, ship } = ctx;
    const nav = computer.navigationBasis(ship);
    if (!nav.target || !nav.targetRelativeVelocity) {
      return {
        duration: 0,
        propellant: 0,
        deltaV: 0,
        peakAcceleration: 0,
        heatLoad: 0,
        failureModes: ['No target selected'],
        blocked: true,
        blockedReason: 'No docking target selected',
      };
    }
    const distance = computer.targetPosition!.distanceTo(ship.body.position);
    const relSpeed = nav.targetRelativeVelocity.length();
    const deltaV = relSpeed + distance * 0.02 + 4;
    const rcsThrust = ship.rcsThrusters.reduce((s, e) => s + e.def.maxThrust, 0);
    const mass = ship.massProperties.total;

    return {
      duration: distance / Math.max(0.5, Math.min(20, distance * 0.05)) + relSpeed * 3 + 45,
      propellant: (deltaV * mass) / (240 * g0),
      deltaV,
      peakAcceleration: rcsThrust / mass,
      heatLoad: 0,
      failureModes: ['Monopropellant depleted', 'Target manoeuvred out of the approach corridor'],
      blocked: ship.fuelOfType('monopropellant') <= 0,
      blockedReason: ship.fuelOfType('monopropellant') <= 0 ? 'Monopropellant depleted' : null,
    };
  }

  protected override onStart(): void {
    this.advance(1, 'Matching velocity');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);

    const { ship, computer } = ctx;
    if (!computer.targetPosition || !computer.targetVelocity) {
      return this.fail(ctx, 'Target lost');
    }
    if (ship.fuelOfType('monopropellant') <= 0) {
      return this.fail(ctx, 'Monopropellant depleted');
    }

    const toTarget = Vec3.subVectors(computer.targetPosition, ship.body.position);
    const distance = toTarget.length();
    const relVel = Vec3.subVectors(ship.body.velocity, computer.targetVelocity);
    const closingSpeed = distance > 1e-6 ? -relVel.dot(toTarget) / distance : 0;

    computer.setAlignment('target');

    if (distance < this.captureDistance && relVel.length() < this.captureSpeed) {
      this.advance(4, 'Capture');
      this.releaseControls(ctx);
      this.statusText = `Docked at ${relVel.length().toFixed(2)} m/s relative`;
      return 'complete';
    }

    // Approach speed profile: fast far away, slow close in, always low enough
    // that the RCS can stop the vessel before contact.
    const desiredClosing = clamp(Math.sqrt(Math.max(0, distance - this.captureDistance)) * 0.9, 0.15, 24);
    const approachDir = toTarget.clone().mulScalar(1 / Math.max(1e-6, distance));

    computer.holds.velocityHold = true;
    computer.holds.matchTargetVelocity = false;
    computer.holds.velocityTarget
      .copy(computer.targetVelocity)
      .addScaled(approachDir, desiredClosing);

    if (distance < 60) this.advance(3, 'Final approach');
    else this.advance(2, 'Approach');

    this.statusText = `${distance.toFixed(1)} m, closing ${closingSpeed.toFixed(2)} m/s (target ${desiredClosing.toFixed(2)})`;
    return 'running';
  }
}

/** Holds a hover at the current altitude until cancelled. */
export class HoverMacro extends BaseMacro {
  readonly id = 'hover';
  readonly name = 'Hold Hover';
  readonly description = 'Maintains the current altitude above terrain using landing thrusters and the field drive.';
  readonly phases = ['Hover'];

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, env } = ctx;
    const g = env.gravity.length();
    const mass = ship.massProperties.total;
    const fieldAccel = ship.fieldDrive ? ship.fieldDrive.availableAcceleration(g) : 0;
    const thrustAccel = ship.landingThrusters.reduce((s, e) => s + e.def.maxThrust, 0) / mass;
    const blocked = thrustAccel + fieldAccel < g;
    // Propellant burn per second for the share the thrusters must carry.
    const chemicalShare = Math.max(0, g - fieldAccel) * mass;
    const isp = Math.max(1, ...ship.landingThrusters.map((e) => e.def.ispVacuum));

    return {
      duration: Infinity,
      propellant: chemicalShare / (isp * g0),
      deltaV: 0,
      peakAcceleration: g,
      heatLoad: 0,
      failureModes: ['Propellant exhausted', 'Field drive thermal cutoff', 'Electrical power exhausted'],
      blocked,
      blockedReason: blocked ? 'Available lift is less than local weight' : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    ctx.computer.holds.altitudeHold = true;
    ctx.computer.holds.altitudeTarget = Math.max(10, ctx.env.altitude);
    ctx.computer.setAlignment('surfaceUp');
    ctx.ship.controls.landingThrustersArmed = true;
    this.advance(0, 'Hovering');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);
    if (ctx.ship.massProperties.fuel <= 0.5 && (ctx.ship.fieldDrive?.availableAcceleration(ctx.env.gravity.length()) ?? 0) < ctx.env.gravity.length()) {
      return this.fail(ctx, 'Propellant exhausted while hovering');
    }
    this.statusText = `Holding ${ctx.computer.holds.altitudeTarget.toFixed(0)} m (actual ${ctx.env.altitude.toFixed(0)} m)`;
    return 'running';
  }
}

/** Lowers periapsis into the atmosphere or onto the surface for a landing. */
export class DeorbitMacro extends BaseMacro {
  readonly id = 'deorbit';
  readonly name = 'Deorbit';
  readonly description = 'Burns retrograde until the periapsis drops to the selected altitude.';
  readonly phases = ['Align', 'Burn', 'Complete'];

  /** Target periapsis altitude, metres. Negative values target the surface. */
  targetPeriapsis = -5000;

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, body } = ctx;
    const r = ship.body.position.length();
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
    const rTarget = Math.max(body.radius * 0.4, body.radius + this.targetPeriapsis);
    const aNew = (r + rTarget) / 2;
    const vNew = Math.sqrt(Math.max(0, body.mu * (2 / r - 1 / aNew)));
    const vNow = ship.body.velocity.length();
    const deltaV = Math.abs(vNow - vNew);
    const isp = Math.max(1, ...ship.mainEngines.map((e) => e.def.ispVacuum));
    const mass = ship.massProperties.total;

    void elements;
    return {
      duration: 90,
      propellant: mass * (1 - Math.exp(-deltaV / (isp * g0))),
      deltaV,
      peakAcceleration: ship.mainEngines.reduce((s, e) => s + e.def.maxThrust, 0) / mass,
      heatLoad: 0,
      failureModes: ['Insufficient delta-v', 'Periapsis still above the atmosphere after the burn'],
      blocked: deltaV > ship.totalDeltaV(),
      blockedReason: deltaV > ship.totalDeltaV() ? 'Insufficient delta-v' : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    ctx.computer.setAlignment('retrograde');
    ctx.ship.controls.mainEngineArmed = true;
    this.advance(0, 'Aligning retrograde');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);

    const { ship, computer, body } = ctx;
    computer.setAlignment('retrograde');

    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
    const periAlt = periapsisRadius(elements) - body.radius;

    if (periAlt <= this.targetPeriapsis) {
      ship.controls.throttle = 0;
      ship.controls.mainEngineArmed = false;
      this.advance(2, 'Complete');
      this.statusText = `Periapsis now ${(periAlt / 1000).toFixed(1)} km`;
      this.releaseControls(ctx);
      return 'complete';
    }

    if (ship.massProperties.fuel <= 1) {
      return this.fail(ctx, 'Propellant exhausted during deorbit burn');
    }

    // Retrograde alignment must be good before the engine lights, otherwise the
    // burn pushes the orbit sideways.
    const alignment = ship.body.forward().dot(ship.body.velocity.normalized().negate());
    ship.controls.throttle = alignment > 0.985 ? clamp01((periAlt - this.targetPeriapsis) / 40000 + 0.15) : 0;
    this.advance(ship.controls.throttle > 0 ? 1 : 0, `Periapsis ${(periAlt / 1000).toFixed(1)} km, target ${(this.targetPeriapsis / 1000).toFixed(1)} km`);
    return 'running';
  }
}
